import { useContext, useMemo } from 'react';
import _ from 'lodash';
import useAPI from 'services/useAPI';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import createQueryRequest from 'services/createQueryRequest';
import { addModelsAndAssetsToDashboard } from './DashboardUtils';
import Tl from 'localization/LocalizationKeys';
import createMutationRequest from 'utils/createMutationRequest';
import { useTranslation } from 'localization/useTranslation';
import AlertContext from 'services/AlertContext';

// export const replaceName = name => name.replace(/@home@/, '');
export const replaceName = name => _.replace(name, /@home@/, '');

//
// Make some adjustments on dashboard data
// - strip @home@ tag from name
//
export const adjustDashboard = (dashboard) => {
  const showOnHomeHack = (dashboard?.displayName ?? dashboard.name)?.search('@home@') !== -1;

  // remove @home@ tag hack
  const displayName = replaceName(dashboard.displayName ?? dashboard.name);

  if (dashboard.data?.version === 2) {
    if (showOnHomeHack) {
      return {
        ...dashboard,
        displayName,
        data: { ...dashboard.data, showOnHome: dashboard.data?.showOnHome ?? showOnHomeHack }
      };
    }
    // Nothing changed
    return dashboard;
  }

  const adjustedDashboard = addModelsAndAssetsToDashboard(dashboard);

  return {
    ...adjustedDashboard,
    displayName,
    data: { ...dashboard.data, showOnHome: dashboard.data?.showOnHome ?? showOnHomeHack }
  };
};

const adjustDashboards = (dashboards) => {
  return _.map(dashboards, adjustDashboard);
};

// export const DashboardsQueryKey = ['dashboards'];

export const DashboardsQueryKey = {
  all: ['dashboards'],
  dashboards: () => [...DashboardsQueryKey.all],
  dashboard: (dashboardId) => [...DashboardsQueryKey.all, dashboardId]
};

const createDashboardsQuery = (api, options) => ({
  queryKey: DashboardsQueryKey.dashboards(),
  queryFn: createQueryRequest(api.core.getDashboards),
  meta: {
    errorMessage: Tl.dashboardspage.error.failedload
  },
  ...options
});

const useDashboardsQuery = (options) => {
  const api = useAPI();
  const query = useQuery(createDashboardsQuery(api, { refetchOnWindowFocus: false, ...options }));
  const data = useMemo(() => query.data ? adjustDashboards(query.data) : [],
    [query.data]);

  return {
    ...query,
    data
  };
};

export const useDeleteDashboard = () => {
  const api = useAPI();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMutationRequest(api.core.deleteDashboard),
    onMutate: (deleteItem) => {
      // Optimistic delete
      const oldDashboardData = [...queryClient.getQueryData(DashboardsQueryKey.dashboards())];
      queryClient.setQueryData(DashboardsQueryKey.dashboards(), oldDashboards => {
        return _.reject(oldDashboards, { _key: deleteItem.id });
      });
      return { deleteItem, oldDashboardData };
    },
    onSuccess: () => {
      return queryClient.invalidateQueries(DashboardsQueryKey.dashboards());
    },
    onError: (unusedError, unusedVariables, context) => {
      // Readd the old data
      queryClient.setQueryData(DashboardsQueryKey.dashboards(), context?.oldDashboardData);
    }
  });
};

export const useDashboardQuery = (dashboardId) => {
  const api = useAPI();
  const T = useTranslation();

  return useQuery({
    queryKey: DashboardsQueryKey.dashboard(dashboardId),
    queryFn: createQueryRequest(api.core.getDashboard),
    initialData: {
      displayName: T.dashboardspage.editor.newdashboard,
      data: { panels: [], version: 1 }
    },
    refreshOnWindowFocus: false,
    enabled: !!dashboardId
  });
};

export const useUpdateDashboard = () => {
  const api = useAPI();
  const queryClient = useQueryClient();
  const { addSuccessAlert, addErrorAlert } = useContext(AlertContext);
  const T = useTranslation();
  return useMutation({
    mutationFn: createMutationRequest(api.core.saveDashboard),
    onMutate: (dashboard) => {
      if (dashboard.id) {
        const oldDashboardData = queryClient.getQueryData(DashboardsQueryKey.dashboard(dashboard.id));
        queryClient.setQueryData(DashboardsQueryKey.dashboard(dashboard.id), dashboard);
        return { dashboard, oldDashboardData };
      }
      return { dashboard };
    },
    onSuccess: (result, savedDashboard) => {
      addSuccessAlert(T.dashboardspage.editor.success.saveddashboard);
      const dashboard = result.id ? result : savedDashboard;
      queryClient.setQueryData(DashboardsQueryKey.dashboard(dashboard.id), dashboard);
      queryClient.invalidateQueries({ queryKey: DashboardsQueryKey.dashboard(dashboard.id) });
      return queryClient.invalidateQueries({ queryKey: DashboardsQueryKey.dashboards() });
    },
    onError: (error) => {
      console.error(error);
      addErrorAlert(T.dashboardspage.editor.error.saveddashboard);
    }
  });

};

export default useDashboardsQuery;
